import React, { ReactNode } from 'react';
import { Action } from 'redux';
import { ThunkAction } from '@reduxjs/toolkit';
import {
  ReservationSlotState,
  ReservationSlotStatus,
  RenovationExecutor,
  BoardMemberStatuses,
  CaseStatuses,
  RepairStatuses,
  TaskStatusCode,
  NotificationTypeCode,
  PortalsName,
  RequestTypeCode,
  CaseExternalStatusCode,
} from 'utils/enums';
import { ContactInformationWidgetModel } from './ContactInformationWidgetModel';
import { ServerResponseBudgetHistory } from './BudgetHistory';

import type { TodoState } from 'store/todo/reducer';

import type { Option as CommonOption } from 'shared/models/common';
import type {
  CaseLevel1Option,
  CaseLevel2Option,
  CaseLevel3Option,
} from 'shared/models/common-cases';
import {
  CaseLevel1Code,
  CaseLevel2Code,
  CaseLevel3Code,
} from 'shared/constants/common-cases';

export type { ActionTableSortParams } from 'components/action-table';

export type RequestStatus = 'idle' | 'loading' | 'error';

type ObjectKeys<T> = T extends object
  ? (keyof T)[]
  : T extends number
  ? []
  : T extends Array<any> | string
  ? string[]
  : never;

declare global {
  interface ObjectConstructor {
    keys<T>(o: T): ObjectKeys<T>;
  }
}

export type ReportId = 'cooperative-budget' | 'cooperative-balance';

export type BaseResponse<T> = Record<'Data', T>;

export type ReportControlField = {
  value: number | string;
  label: string;
};

export type ActionTableSortType = 'alphanumeric' | 'datetime' | 'array';
export type OptionalDate = Date | null;
export type TableFieldType =
  | 'string'
  | 'hashstring'
  | 'date'
  | 'datetime'
  | 'number'
  | 'space'
  | 'html'
  | 'list';

// style applies to table headers
// rowStyle applies to table rows
// while logicalName is undefined fields will sort by id
// small - if true, field stays in table head while 768* > viewport >= 475*
// xs - if true, field stays in table head while 475* > viewport
// 768*, 475* custom theme breakpoints (defined in configs/theme)

export type TableCells<T = {}> = {
  /**
   * Key of object
   */
  id: string;
  /**
   * If logicalName has some value data will be sorted by this field
   */
  logicalName?: string;
  /**
   * Key code for translation
   */
  label: string;
  /**
   * Render cell view depends of type
   */
  type: TableFieldType;
  format?: string;
  /**
   * If `true`, can sort by id or logicalName
   */
  canSort?: boolean;
  canSearch?: boolean;
  /**
   * Render custom content of table cell
   * @param {object} item - current data object
   * @param {string} id - key of object
   */
  render?: (item: T, id: string) => ReactNode;
  /**
   * Styles applied to table header cell
   */
  style?: React.CSSProperties;
  /**
   * Styles applied to table row cell
   */
  rowStyle?: React.CSSProperties;
  small?: boolean;
  smallStyles?: React.CSSProperties;
  xs?: boolean;
  xsStyles?: React.CSSProperties;
  defaultLabelRegister?: boolean;
  searchKey?: string;
};

export type ModalState = {
  open: boolean;
  caseId: string | null;
};

export type MeterIndication = {
  MeterId: string | null;
  Value: number | null;
  Date: string | null;
  IndicationId: string | null;
};

export interface PasswordResetForm {
  email: '';
}

export interface PasswordResetErrors {
  ErrorCode: number | null;
  ErrorMessage: string | null;
}

export type IndicationStatus = 0 | 1 | 2 | 3 | 4 | 5 | 6;

export type ValidatedMeter = MeterIndication & { Status: IndicationStatus };

export type CreateIndicationValidationRes = {
  Status: IndicationStatus;
  Indication: MeterIndication;
};

export type Money = {
  Value: number | null;
  Symbol: string | null;
  Code: string | null;
};

export type ReservationCalendarDay = {
  Date: string | null;
  StartTime: string | null;
  EndTime: string | null;
  SlotDuration: number | null;
  Slots: ReservationSlot[];
};

export type ReservationSlot = {
  DisplayText: string | null;
  StartTime: string | null;
  EndTime: string | null;
  State: ReservationSlotState | null;
  Status: ReservationSlotStatus | null;
  IsActivity: boolean | null;
  SourceId: string | null;
  SourceLogicalName: string | null;
  CanReserve: boolean | null;
  CanCancel: boolean | null;
  IsOwner: boolean | null;
};

export type CustomSlot = ReservationSlot & {
  Duration: number | null;
  Price: number | null;
};

export interface PartialSlot {
  Customers: EntityRef[];
  DisplayName: string | null;
  EndTime: string | null;
  Id: string | null;
  LogicalName: string | null;
  StartTime: string | null;
}

export type ReservationServiceResourceView = {
  Id: string;
  Name: string | null;
  Description: string | null;
  SlotPrice: Money | null;
  ImagesUrl: string[];
};

export type MeterModel = {
  Id: string;
  MeterId: string | null;
  MeterNumber: number | null;
  MeterType: Option | null;
  Building: EntityRef | null;
  Space: EntityRef | null;
  SpaceSortField: string | null;
  Location: string | null;
  DateOfLastIndication: string | null;
  IndicationId: string | null;
  LastIndication: number | null;
  Indication: number | null;
};

export type ReservationServiceView = {
  Id: string;
  Service: EntityRef | null;
  CooperativeIds: string[];
  Address: string | null;
  Name: string | null;
  Description: string | null;
  SlotDuration: number | null;
  IsSchedulable: boolean | null;
  Resources: ReservationServiceResourceView[];
  ImagesUrl: string[];
};

type Address = {
  Country: string | null;
  Id: string | null;
  PostalArea: string | null;
  PostalCode: string | null;
  Street1: string | null;
  Street2: string | null;
  Street3: string | null;
};

export type BankAccount = {
  Id: string;
  IsCommission: boolean;
  IsReimbursement: boolean;
  Number: string | null;
};

type Email = {
  Email: string | null;
  Id: string | null;
};

type Phone = {
  Id: string | null;
  Phone: string | null;
};

export enum MassMessageSendingMethods {
  None = 0,
  MailingService = 1,
  Email = 2,
  SMS = 4,
  Stickers = 8,
  PortalMessage = 16,
  FloorApp = 32,
}

export type UserSettings = {
  Address: Address | null;
  AllowNotifications: boolean | null;
  AlternativeInvoiceAddress: Address | null;
  AlternativeInvoiceEmail: Email | null;
  AlternativeMailAddress: Address | null;
  AlternativeMailEmail: Email | null;
  BankAccounts: BankAccount[];
  Birthday: string | null;
  CanEditUserSettings: boolean | null;
  Email: Email | null;
  FirstName: string | null;
  FullName: string | null;
  Id: string | null;
  InvoiceChannelPreferred: Option | null;
  LastName: string | null;
  MarketingPermission: boolean | null;
  Phone: Phone | null;
  HasSocialSecurityNumber: boolean;
  Tax: number | null;
  UseAddressForMeeting: boolean | null;
  UseAddressForRelease: boolean | null;
  UseAlternativeMailAddressForMeeting: boolean | null;
  UseAlternativeMailAddressForRelease: boolean | null;
  UseAlternativeMailEmailForMeeting: boolean | null;
  UseAlternativeMailEmailForRelease: boolean | null;
  UseEmailForMeeting: boolean | null;
  UseEmailForRelease: boolean | null;
};

export type BankAccountView = {
  Id: string;
  LogicalName: string | null;
  CooperativeIds: string[];
  Cooperative: EntityRef | null;
  Operator: EntityRef | null;
  Number: string | null;
  Description: string | null;
  IsMain: boolean | null;
  Balance: Money | null;
  BalanceDate: string | null;
  ModifiedOn: string;
};

export type FormFields<T extends {}> = {
  id: keyof T;
  label: string;
  validation?: 'required';
  type: 'select' | 'text' | 'number' | 'checkbox' | 'uploader';
  optionsField?: keyof T;
  disabled?: boolean;
  labelField?: 'Label';
  valueField?: 'Value';
};

export type FormConfig<T extends {}> = {
  title: string;
  root: 'column' | 'row';
  boxes: {
    left: FormFields<T>[];
    right: FormFields<T>[];
  };
};

export type EventInformation = {
  ActualDuration: string | null;
  ActualEnd: string | null;
  ActualStart: string | null;
  AuditAuditingDone: string | null;
  AuditDeliveryDate: string | null;
  AuditReturnNeededDate: string | null;
  CooperativeIds: string[];
  Cooperative: EntityRef | null;
  Documents: Record<string, string> | null;
  Id: string;
  LastMessageDistributionDate: string | null;
  LogicalName: string;
  Name: string | null;
  PlannedStart: string | null;
  PlannedEnd: string | null;
  PlannedDuration: string | null;
  Status: Option | null;
  Type: EntityRef | null;
};

export type AloneEventInformation = EventInformation & {
  Location: string | null;
  Description: string | null;
};

export type ModifiedEventInformation = EventInformation & {
  ActualTime: string | null;
  PlannedTime: string | null;
};

export type RenovationTypesCodes =
  | 752560000
  | 752560001
  | 752560003
  | 752560004
  | 752560005
  | 752560006
  | 752560007
  | 752560008
  | 752560009
  | 752560010
  | 752560011
  | 752560012
  | 752560013
  | 752560014
  | 752560015
  | 752560016
  | 752560017
  | 752560018
  | 752560019
  | 752560020;

export interface RenovationType {
  Label: string;
  Value: RenovationTypesCodes;
}

export type RenovationTypePortalUser = {
  FirstName: string | null;
  LastName: string | null;
  Birthday: string | null;
  Email: string | null;
  MobilePhone: string | null;
};

export interface ModifiedRenovationType extends RenovationType {
  Parent: string;
}

export type RenovationTypeCreateModel = {
  Type: number | null;
  ExecutorType: number | null;
  Description: string | null;
};

export type RenovationTypeExecutorCodeModel = {
  Label: string;
  Value: RenovationExecutor;
};

export type PortalUserInfo = {
  Birthday: string | null;
  Email: { Id: string | null; Email: string | null } | null;
  FirstName: string;
  LastName: string;
  Phone: { Id: string | null; Phone: string | null } | null;
};

type RepairFetchedFormMetadata = {
  DescriptionMaxLength: number | null;
  ForeignRegistrationNumberMaxLength: number | null;
};

export type RepairFetchedForm = {
  AirSourceHeatPumpAssembly: boolean;
  BalconyOrTerraceGlazing: boolean;
  ChangesOnFloorPlan: boolean;
  ChangesOnWetSpaces: boolean;
  Description: string | null;
  Electrics: boolean;
  EndDate: string | null;
  FormMetadata: RepairFetchedFormMetadata;
  OtherMinorChange: boolean;
  OtherOrdinaryChange: boolean;
  OtherSignificantChange: boolean;
  Painting: boolean;
  Plumbing: boolean;
  PortalUserInfo: PortalUserInfo | null;
  RenewalOfFloorCoating: boolean;
  RenewalOfInteriorDoors: boolean;
  SendEmail: boolean;
  SendSms: boolean;
  SpaceId: string | null;
  Spaces: Option[];
  StartDate: string | null;
  Wallpapering: boolean;
  RenovationTypesCodes: RenovationType[];
  RenovationTypes: RenovationTypeCreateModel[] | null;
  RenovationTypeExecutorsCodes: RenovationTypeExecutorCodeModel[];
};

export type RepairFormType = {
  AirSourceHeatPumpAssembly: boolean;
  BalconyOrTerraceGlazing: boolean;
  ChangesOnFloorPlan: boolean;
  ChangesOnWetSpaces: boolean;
  Description: string | null;
  Electrics: boolean;
  EndDate: Date | null;
  OtherMinorChange: boolean;
  OtherOrdinaryChange: boolean;
  OtherSignificantChange: boolean;
  Painting: boolean;
  Plumbing: boolean;
  RenewalOfFloorCoating: boolean;
  RenewalOfInteriorDoors: boolean;
  RenovationTypes: RenovationTypeCreateModel[] | null;
  SendEmail: boolean;
  SendSms: boolean;
  SpaceId: Option | null;
  Spaces: Option[];
  StartDate: Date | null;
  Wallpapering: boolean;
};

export type RepairNewFormRenovationType = {
  Type: RenovationType | null;
  ExecutorType: RenovationTypeExecutorCodeModel | null;
  Description: string | null;
  Email: string;
  MobilePhone: string;
  OrganizationName: string;
  BusinessId: string;
  FirstName: string;
  LastName: string;
  Birthday: Date | null;
};

export type RepairNewForm = {
  Space: Option | null;
  Spaces: Option[];
  StartDate: Date | null;
  EndDate: Date | null;
  Description: string;
  RenovationTypes: RepairNewFormRenovationType[];
  Plumbing: boolean;
  Electrics: boolean;
  ChangesOnWetSpaces: boolean;
  BalconyOrTerraceGlazing: boolean;
  ChangesOnFloorPlan: boolean;
  OtherSignificantChange: boolean;
  RenewalOfFloorCoating: boolean;
  AirSourceHeatPumpAssembly: boolean;
  OtherOrdinaryChange: boolean;
  Painting: boolean;
  RenewalOfInteriorDoors: boolean;
  Wallpapering: boolean;
  OtherMinorChange: boolean;
  SendEmail: boolean;
  SendSms: boolean;
};

export type BoardMemberStatus = {
  Label: string;
  Value: BoardMemberStatuses;
};

export type BoardMemberTaskModel = {
  ConclusionDay: string | null;
  ExecutorName: string | null;
  ExecutorRole: string | null;
  IsAutomaticallyManaged: boolean | null;
  Status: BoardMemberStatus | null;
};

export type CaseBaseSpaceRenovation = {
  BoardMemberTasks: BoardMemberTaskModel[];
  Building: EntityRef | null;
  BuildingAddress: string | null;
  ConclusionDate: string | null;
  ContactPerson: EntityRef | null;
  Cooperative: EntityRef | null;
  CooperativeIds: string[];
  CreatedOn: string | null;
  DescriptionOfTerms: string | null;
  EstimatedTiming: string | null;
  Id: string;
  IncludeInCooperativeReport: boolean;
  IsAllowEditResolutionAndAddFiles: boolean;
  IsBoardMemberContactPerson: boolean;
  IsShowApproveReject: boolean;
  LogicalName: string | null;
  ModifiedOn: string | null;
  Organization: EntityRef | null;
  Priority: Option | null;
  ProjectStatus: Option | null;
  PublicDescription: string | null;
  RenovationTypes: RenovationTypeViewModel[];
  RequestType: Option | null;
  ResolutionDescription: string | null;
  Space: EntityRef | null;
  SpaceTypeDescription: Option | null;
  State: Option | null;
  Status: Option | null;
  Supervisor: CaseRepairSupervisor | null;
  Title: string | null;
};

export type CaseBaseModel = {
  Id: string | null;
  LogicalName: string | null;
  Title: string | null;
  CaseLevel1: CaseLevel1Option | null;
  RequestType: {
    Label: string;
    Value: RequestTypeCode;
  } | null;
  Priority: Option | null;
  Cooperative: EntityRef | null;
  Space: EntityRef | null;
  Status: {
    Label: string;
    Value: CaseStatuses;
  } | null;
  State: Option | null;
  CreatedOn: string | null;
  ModifiedOn: string | null;
  ConclusionDate: string | null;
};

export type ListModelGuid = {
  Label: string | null;
  Value: string | null;
};

export interface BaseMassMessage {
  Id: string;
  LogicalName: string | null;
  CooperativeIds: string[];
  EventType: EntityRef | null;
  Cooperative: EntityRef | null;
  Subject: string | null;
  Description: string | null;
  Status: Option | null;
  DistributedOn: string | null;
  CreatedOn: string;
  ModifiedOn: string | null;
}

export interface ExtendedMassMessage extends BaseMassMessage {
  EventInformationUrl: string | null;
  EventInformation: EntityRef | null;
  FloorAppMessageDue: string | null;
  IsTalotiliCoop: boolean | null;
  PortalMessageDue: string | null;
  RecipientGroupsValues: RecipientGroupViewModel[] | null;
  RecipientRolesValues: ListModelGuid[] | null;
  SendSms: boolean | null;
  SendEmail: boolean | null;
  SendPortalMessage: boolean | null;
  SendFloorApp: boolean | null;
  RecipientRoles: string[] | null;
  Spaces: string[];
}

export interface MassMessage extends BaseMassMessage {
  EventInformationUrl: string | null;
  EventInformation: EntityRef | null;
  FloorAppMessageDue: string | null;
  IsTalotiliCoop: boolean | null;
  PortalMessageDue: string | null;
  RecipientGroupsValues: RecipientGroupViewModel[] | null;
  RecipientRolesValues: ListModelGuid[] | null;
  SendSms: boolean | null;
  SendEmail: boolean | null;
  SendPortalMessage: boolean | null;
  SendFloorApp: boolean | null;
  RecipientRoles: MassMessageRole[];

  Spaces: MassMessageSpace[];
}

export type MassMessageRecepientValueType = 'default' | 'apply' | 'reject';

export type MassMessageCreateModel = {
  AvailableSendingMethods: MassMessageSendingMethods[];
  CooperativeId: string | null;
  Description: string | null;
  DescriptionPlainText: string | null;
  FloorAppMessageDue: string | null;
  FormMetadata: FormMetadataModel;
  PortalMessageDue: string | null;
  RecipientRoles: string[];
  SendEmail: boolean | null;
  SendFloorApp: boolean | null;
  SendPortalMessage: boolean | null;
  SendSms: boolean | null;
  Spaces: string[];
  Subject: string | null;
  IsTalotiliCoop: boolean;
};

export type EventModel = {
  Id: string;
  LogicalName: string | null;
  Subject: string | null;
  Description: string | null;
  Cooperative: EntityRef | null;
  CooperativeIds: string[] | null;
  IsRead: boolean | null;
  CreatedOn: string | null;
  ModifiedOn: string | null;
  Documents: Record<string, string> | null;
};

export type MassMessageCreateFormModel = {
  CooperativeId: string | null;
  Subject: string;
  Description: string;
  SendSms: boolean | null;
  SendEmail: boolean | null;
  SendPortalMessage: boolean | null;
  SendFloorApp: boolean | null;
  RecipientRoles: MassMessageRole[];
  Spaces: MassMessageSpace[];
  PortalMessageDue: Date | null;
  FloorAppMessageDue: Date | null;
};

export type NamedValueString = {
  Name: string | null;
  Value: string | null;
};

export type NamedValueInteger = {
  Name: string | null;
  Value: number | null;
};

export enum ConnectionTargetTypeCode {
  Cooperative = 1,
  Space = 2,
}

export enum RecipientContactMethod {
  Contact = 100000000,
  Mailing = 100000001,
  Main = 100000002,
  Invoice = 100000003,
  EInvoiceReceive = 100000004,
}

export enum RecipientContactType {
  Address = 100000000,
  EInvoiceCustomer = 100000001,
  EInvoiceOrganization = 100000009,
  DirectPayment = 100000008,
  Email = 100000002,
  BusinessPhone = 100000003,
  HomePhone = 100000004,
  MobilePhone = 100000005,
  Fax = 100000007,
  WebAddress = 100000006,
  EmergencyPhone = 100000010,
}

export enum RecipientType {
  Person = 1,
  Organization = 2,
  User = 3,
}

export interface MassMessageRecipientConnection {
  CreatedOn: string;
  Id: string;
  ModifiedOn: string;
  Role: string | null;
  RoleId: string | null;
  Target: string | null;
  TargetId: string | null;
  TargetType: ConnectionTargetTypeCode;
}

export interface MassMessageRecipientContact {
  Contact: string | null;
  CreatedOn: string;
  Id: string;
  IsMeetingInvitation: boolean | null;
  IsRelease: boolean | null;
  Method: RecipientContactMethod | null;
  ModifiedOn: string;
  Type: RecipientContactType;
}

export interface MassMessageRecipient {
  Connections: MassMessageRecipientConnection[];
  Contacts: {
    SendingMethod: MassMessageSendingMethods;
    Contact: MassMessageRecipientContact | null;
  }[];
  FullName: string;
  Id: string;
  Type: RecipientType;
}

export type CaseExternalStatusModel = 1 | 2 | 3 | 4 | null;

export type EnhancedOption<T> = {
  Label: string | null;
  Value: T;
};

export type CaseExternalModel = {
  AdditionalInformation: string | null;
  AmountOfKeys: number | null;
  AppartmentHasAdditionalLock: boolean;
  BuildingAddress: string | null;
  BuildingPostalArea: string | null;
  BuildingPostalCode: string | null;
  CaseLevel1: Option | null;
  CaseLevel2: Option | null;
  CaseLevel3: Option | null;
  CaseStatus: Option | null;
  ContactPersonEmail: string | null;
  ContactPersonMobilePhone: string | null;
  ContactPersonRef: EntityRef | null;
  CreatedOn: string | null;
  CustomerDescription: string | null;
  CustomerRef: EntityRef | null;
  DescriptionOfTerms: string | null;
  EstimatedTiming: string | null;
  IsFromTask: boolean;
  KeyNumber: string | null;
  KeyOrderType: Option | null;
  MasterKeyAllowed: boolean;
  Number: string | null;
  RenovationTypes: string | null;
  RepairFormNeedToBeAgreed: boolean;
  RequestType: EnhancedOption<RequestTypeCode> | null;
  SpaceRef: EntityRef | null;
  Status: CaseExternalStatusCode | null;
  TypeOfTheKey: string | null;
};

export type ContactInformation = {
  Id: string;
  LogicalName: string | null;
  ContactMethod: NamedValueInteger | null;
  ContactType: NamedValueInteger | null;
  ContactInfo: NamedValueInteger | null;
};

export type FormMetadataModel = {
  Url: string | null;
  NeedAttachments: boolean | null;
  Method: string[];
};

export type RecipientGroupViewModel = {
  Id: string;
  Name: string | null;
  Type: 1 | 2 | 3;
  Childs: RecipientGroupViewModel[];
};

export type RecipientBody = {
  CooperativesIds: string[];
  SpacesIds: string[];
  RecipientRolesIds: string[];
  IgnoredRecipientRolesIds: string[];
  SendEmail: boolean;
  SendSms: boolean;
  SendPortalMessage: boolean;
  SendFloorApp: boolean;
};

export type RecipientChangeModel = {
  cooperatives: string[];
  spaces: string[];
  buildings: string[];
};

export type CaseRepairSupervisor = {
  Id: string | null;
  Name: string | null;
  Email: string | null;
  MobilePhone: string | null;
};

export type RenovationTypeViewModel = {
  Id: string | null;
  Type: RenovationType | null;
  ExecutorType: RenovationTypeExecutorCodeModel | null;
  Description: string | null;
  OrganizationName: string | null;
  BusinessId: string | null;
  FirstName: string | null;
  LastName: string | null;
  FullName: string | null;
  Birthday: string | null;
  Email: string | null;
  MobilePhone: string | null;
};

export type Repair = {
  ApprovalStatus: Option | null;
  Supervisor: CaseRepairSupervisor | null;
  RenovationTypes: RenovationTypeViewModel[];
  BuildingAddres: string | null;
  DescriptionOfTerms: string | null;
  ResolutionDescription: string | null;
  ProjectStatus: Option | null;
  StartDate: string | null;
  EndDate: string | null;
  Description: string | null;
  Plumbing: boolean | null;
  Electrics: boolean | null;
  ChangesOnWetSpaces: boolean | null;
  BalconyOrTerraceGlazing: boolean | null;
  ChangesOnFloorPlan: boolean | null;
  OtherSignificantChange: boolean | null;
  RenewalOfFloorCoating: boolean | null;
  AirSourceHeatPumpAssembly: boolean | null;
  OtherOrdinaryChange: boolean | null;
  IncludeInCooperativeReport: boolean | null;
  EstimatedTiming: string | null;
  PublicDescription: string | null;
  Painting: string | null;
  RenewalOfInteriorDoors: boolean | null;
  Wallpapering: boolean | null;
  OtherMinorChange: boolean | null;
  SendSms: boolean | null;
  SendEmail: boolean | null;
  Id: string;
  LogicalName: string | null;
  Title: string | null;
  RequestType: Option | null;
  Priority: Option | null;
  Cooperative: EntityRef | null;
  ContactPerson: EntityRef | null;
  Space: EntityRef | null;
  Status: {
    Label: string;
    Value: RepairStatuses;
  } | null;
  State: Option | null;
  CreatedOn: string | null;
  ModifiedOn: string | null;
};

export interface CommonCaseTask {
  Executor: EntityRef | null;
  PMCompany: EntityRef | null;
  IsAutomaticallyManaged: boolean | null;
  StatusInfo: string | null;
  Status: Option | null;
}

export type CommonCase = {
  Description: string | null;
  IsMasterKeyAllowed: boolean | null;
  IsRepairTimeNeedToBeAgreed: boolean | null;
  IsAppartmentHasAdditionalLock: boolean | null;
  SendSms: boolean | null;
  SendEmail: boolean | null;
  Id: string;
  LogicalName: string | null;
  Title: string | null;
  RequestType: Option | null;
  Priority: Option | null;
  Cooperative: EntityRef | null;
  HasFiles: boolean;
  Space: EntityRef | null;
  Status: CommonOption<CaseStatuses> | null;
  State: Option | null;
  CreatedOn: string | null;
  ModifiedOn: string | null;
  CaseLevel1: CaseLevel1Option | null;
  CaseLevel2: CaseLevel2Option | null;
  CaseLevel3: CaseLevel3Option | null;
  Tasks: CommonCaseTask[] | null;
};

export interface CaseCommonViewListModel {
  Cases: CommonCase[];
  CaseLevels1: CaseLevel1Option[] | null;
  CaseLevels2: CaseLevel2Option[] | null;
}

export type CommonCaseFetchedForm = {
  Spaces: Option[] | null;
  CaseLevels1: CaseLevel1Option[] | null;
  CaseLevels2: CaseLevel2Option[] | null;
  CaseLevels3: CaseLevel3Option[] | null;
  SpaceId: string | null;
  CooperativeId: string | null;
  CaseLevel1: number | null;
  CaseLevel2: number | null;
  CaseLevel3: number | null;
  Description: string;
  IsMasterKeyAllowed: boolean | null;
  IsRepairTimeNeedToBeAgreed: boolean | null;
  IsAppartmentHasAdditionalLock: boolean | null;
  SendSms: boolean | null;
  SendEmail: boolean | null;
  HasDocuments: boolean | null;
};

export type BaseCommonCaseFormType = {
  SpaceId: string | null;
  CooperativeId: null | string;
  Description: null | string;
  SendSms: boolean;
  SendEmail: boolean;
};

export type CommonCaseFormType = {
  SpaceId: string | null | undefined;
  CooperativeId: null | string;
  Description: null | string;
  SendSms: boolean;
  SendEmail: boolean;
  CaseLevel1: CaseLevel1Code;
  CaseLevel2: CaseLevel2Code | null;
  CaseLevel3: CaseLevel3Code | null;
  IsAppartmentHasAdditionalLock: boolean | null;
  IsMasterKeyAllowed: boolean | null;
  IsRepairTimeNeedToBeAgreed: boolean | null;
};

export type DocumentsRes = {
  Id: string;
  Name: string;
  Documents: Document[];
};

export type Document = {
  CanDelete: boolean;
  CreatedOn: string;
  Extension: string;
  Id: string;
  Information: {};
  ModifiedOn: string;
  Name: string;
  Size: number;
};

type AccountDocumentInformation = {
  Information: {
    Information_x0020_Group: string[] | null;
    Service_x002f_Process: string[] | null;
  };
};

export type AccountDocument = Document & AccountDocumentInformation;

export type AccountDocumentsRes = {
  Id: string;
  Name: string;
  Documents: AccountDocument[];
};

export type ChatMessage = {
  Id: string;
  CreatedOn: string;
  IsRead: boolean;
  Message: string;
  UserRef: EntityRef;
  IsShowToContactPerson?: boolean;
};

export type User = {
  FirstName: string;
  LastName: string;
  FullName: string;
  RegistrationState: number;
};

export type KeyOrder = {
  AdditionalInformation: string | null;
  AmountOfKeys: number | null;
  Cooperative: EntityRef | null;
  ContactPerson: EntityRef | null;
  CreatedOn: string | null;
  Id: string;
  KeyNumber: string | null;
  KeyOrderType: Option | null;
  LogicalName: string | null;
  ModifiedOn: string | null;
  Priority: Option | null;
  RequestType: Option | null;
  SendEmail: boolean;
  SendSms: boolean;
  Space: EntityRef | null;
  State: Option | null;
  Status: Option | null;
  Title: string | null;
  TypeOfTheKey: string | null;
};

export type KeyOrderFormType = {
  AmountOfKeys: number | null;
  KeyNumber: string | null;
  KeyOrderType: Option | null;
  KeyOrderTypes: Option[];
  SendEmail: boolean;
  SendSms: boolean;
  SpaceId: Option | null;
  Spaces: Option[];
  TypeOfTheKey: string | null;
};

export type FiscalYear = {
  CooperativeIds: string[];
  EndDate: string;
  Id: string;
  Name: string | null;
  StartDate: string;
};

export type FiscalYearsRes = {
  ClosedPeriodEnd: string | null;
  FiscalYears: FiscalYear[];
  NotMappedPaymentDate: string | null;
};

type ServiceProviderType = 1 | 2;

export interface ServiceProvider {
  Id: string;
  Type: ServiceProviderType | null;
  Role: string | null;
  Name: string | null;
  Order: number | null;
  PostAddress: string | null;
  Email: string | null;
  PhoneNumber: string | null;
  MobilePhoneNumber: string | null;
  MobilePhoneNumberDescription: string | null;
  EmergencyPhoneNumber: string | null;
  EmergencyPhoneNumberDescription: string | null;
  Description: string | null;
  OpeningHours: string | null;
  Substitutes: ServiceProviderSubstitute[];
}

export interface ServiceProviderSubstitute extends ServiceProvider {
  Start: string | null;
  End: string | null;
}

export interface ServiceProvidersGroup {
  Name: string | null;
  Order: number | null;
  ServiceProviders: ServiceProvider[];
}

export interface CooperativeServiceProviders {
  Groups: ServiceProvidersGroup[];
  CooperativeIds: string[];
}

export interface MeterRes {
  StarDate: null | string;
  EndDate: null | string;
  Meters: Meter[];
}

export type InvestorSpaceModel = {
  Address: string | null;
  Cooperative: EntityRef | null;
  Id: string;
  LogicalName: string | null;
  Name: string | null;
  PostalArea: string | null;
  Size: number | null;
  Status: Option | null;
  Type: Option | null;
  TenantRentContractEndDate: string | null;
  TenantRentContractStartDate: string | null;
  Tenants: EntityRef[] | null;
};

export type InvestorSpaceOptionsModel = {
  SpaceStatusOptions: Option[];
  SpaceTypeOptions: Option[];
};

export type InvestorSpaceFilters = {
  uds_cooperativeid: string;
  uds_accessaddress: string;
  uds_spaceaddresscity: string;
  uds_name: string;
  uds_squaremeters_min: string;
  uds_squaremeters_max: string;
  uds_spacetypecode: Option[];
  uds_spacestatuscode: Option[];
};

export type PagingModel = {
  PageNumber: number | null;
  PageSize: number | null;
  TotalCount: number | null;
  HasMore: boolean | null;
};

export type Consumption = {
  Id: string;
  Meter: EntityRef | null;
  Date: string | null;
  Value: number | null;
  Usage: number | null;
};

export type SortParams = {
  order: 'asc' | 'desc';
  orderBy: string;
  type: TableFieldType;
};

export type SortParamsArg = {
  id: string;
  type: TableFieldType;
};

export type Space = {
  Id: string;
  LogicalName: string | null;
  SpaceSortField: string | null;
  RoomsNumberOf: number | null;
  SquareMeters: number | null;
  PetsInSpace: Option | null;
  Cooperative: EntityRef | null;
  Building: EntityRef | null;
  CooperativeIds: string[];
  HasFiles: boolean;
  Name: string | null;
  Type: Option | null;
  Status: Option | null;
  TypeDescription: string | null;
  TypeText: string | null;
  Stairway: string | null;
  Floor: string | null;
  UsageTypeGroup: Option | null;
  CooperativeContractSquareMeters: number | null;
  AdditionalInformation: string | null;
  AddressLine1: string | null;
  SpaceDefiency: string | null;
  ShareNumbers: string | null;
  ShareAmount: number | null;
  ShareNumberOfVotes: number | null;
};

export type Meter = {
  Id: string;
  MeterId: string | null;
  Multiplier: any;
  Type: Option | null;
};

export type AsyncAction = ThunkAction<void, State, unknown, Action<string>>;

export type Translations = {
  [key: string]: {
    [key: string]: string;
  };
};

export type Notification = {
  Id: string;
  Notifications: 0 | 1;
};

interface DefaultFieldItemList<T> {
  align?: 'left' | 'center' | 'right';
  /**
   * Key of passed object
   */
  id: string;
  /**
   * Show or hide notification
   * 0 - hide
   * 1 - show
   */
  notificationCode?: 0 | 1;
  /**
   * Pass a key of object to determine visibillity based on its value
   */
  dependsOn?: string;
  /**
   * Key code for translation
   */
  key: string;
  /**
   * Custom render of item
   */
  renderFullItem?: (val: T) => ReactNode;
  /**
   * Header which renders within current list
   */
  column?: boolean;
  header?: {
    /**
     * Render position of header
     */
    position: 'before' | 'after';
    /**
     * Header text
     */
    text: string;
  };
  show?(val: T): boolean;
  /** If value is true title is visible
   * @default true
   */
  showTitle?: boolean;
}

export type FieldItemList<T = {}> = DefaultFieldItemList<T> &
  (
    | {
        format: 'date' | 'string' | 'datetime' | 'html';
        render?: (val: T) => ReactNode;
      }
    | {
        format: 'list';
        render: (val: any, idx: number) => ReactNode;
      }
    | {
        format: 'boolean';
        variants: [ReactNode, ReactNode];
        render?: (val: T) => ReactNode;
      }
  );

export type FieldItem<T extends object = {}> = {
  fieldsList: FieldItemList<T>[];
  title: string;
};

export type EntityRef = {
  Name: string | null;
  LogicalName: string | null;
  Id: string;
};

export type Option = {
  Label: string | null;
  Value: number | string | null;
};

export type Invoice = {
  Id: string;
  LogicalName: string | null;
  Title: string | null;
  Description: string | null;
  InvoiceId: string | null;
  Date: string | null;
  PaymentDue: string | null;
  TotalAmountVATIncl: Money | null;
  Status: Option | null;
  CurrencySymbol: string | null;
  CooperativeIds: string[];
  Payers: string | null;
  DocumentUrl: string | null;
  Spaces: string | null;
  Type: string | null;
};

export type PurchaseInvoicesSummary = {
  InvoicesCount: number | null;
  TotalAmountOfInvoices: Money | null;
  TotalAmountOfPaidInvoices: Money | null;
  TotalAmountOfUnpaidInvoices: Money | null;
};

export type PurchaseInvoiceModel = {
  ImageLink: string | null;
  Id: string;
  SellerInvoiceNumber: string | null;
  SellerOrganisationName: string | null;
  BuyerAccountNumber: EntityRef | null;
  AccountingDate: string | null;
  InvoiceDueDate: string | null;
  TotalAmount: Money | null;
  PaymentStatus: Option | null;
  RejectComment: string | null;
};

export type LedgerAccountEvent = {
  CooperativeIds: string[];
  Id: string;
  LedgerAccount: EntityRef | null;
  LedgerAccountNumber: string | null;
  Date: string | null;
  LedgerAccountDebet: Money | null;
  LedgerAccountCredit: Money | null;
  Description: string | null;
  CostLocation: string | null;
};

export type PurchaseInvoiceMonthsInformation = {
  Months: PurchaseInvoiceMonthInformation[];
  SellerTotalAmount: Money | null;
  SellerToTotalPropertyMaintenanceCostsСorrelationPersent: number | null;
  TotalPropertyMaintenanceCosts: Money | null;
  FiscalYearStartDate: string | null;
  FiscalYearEndDate: string | null;
};

export type TaskTodoListOptions = {
  Status: CommonOption<number>[];
  Sort: string[];
  CanCreate: boolean | null;
};

export type TaskExecutor = {
  Roles: string[];
  Name: string | null;
  LogicalName: string | null;
  Id: string;
};

export type TaskTodoCreateOptions = {
  Status: Option[];
  Priority: Option[];
  Recurring: Option[];
  Executor: TaskExecutor[];
};

export type TaskTodo = {
  HasFiles: boolean;
  Description: string | null;
  ResolutionDescription: string | null;
  StartDate: string | null;
  Owner: EntityRef | null;
  Cooperative: EntityRef | null;
  Recurring: Option | null;
  ShowOnEventOfBoardMeeting: boolean | null;
  ShowOnEventOfGeneralMeeting: boolean | null;
  IsOwner: boolean | null;
  IsExecutor: boolean | null;
  HasSubTasks: boolean | null;
  Id: string;
  LogicalName: string | null;
  Subject: string | null;
  Executor: EntityRef | null;
  Priority: Option | null;
  Status: {
    Label: string;
    Value: TaskStatusCode;
  } | null;
  State: Option | null;
  DueDate: string | null;
  CreatedOn: string | null;
  ModifiedOn: string | null;
};

export type InitTodoForm = Pick<
  TaskTodo,
  | 'Subject'
  | 'Description'
  | 'ResolutionDescription'
  | 'StartDate'
  | 'DueDate'
  | 'Recurring'
  | 'ShowOnEventOfBoardMeeting'
  | 'ShowOnEventOfGeneralMeeting'
  | 'Executor'
  | 'Status'
  | 'Priority'
>;

export type BaseTaskTodo = {
  Id: string;
  LogicalName: string | null;
  Subject: string | null;
  Executor: EntityRef | null;
  Priority: CommonOption<number> | null;
  Status: CommonOption<number> | null;
  State: CommonOption<number> | null;
  DueDate: string | null;
  CreatedOn: string | null;
  ModifiedOn: string | null;
  HasFiles: boolean;
};

export type UserTaskModel = {
  HasFiles: boolean;
  ContactPerson: EntityRef | null;
  ContactPersonMobilePhone: string | null;
  ContactPersonEmail: string | null;
  Description: string | null;
  CustomerDescription: string | null;
  ResolutionDescription: string | null;
  BuildingAddress: string | null;
  BuildingPostalCode: string | null;
  BuildingPostalArea: string | null;
  IsMasterKeyAllowed: boolean | null;
  IsRepairTimeNeedToBeAgreed: boolean | null;
  IsAppartmentHasAdditionalLock: boolean | null;
  Status: Option | null;
  Id: string;
  LogicalName: string | null;
  Title: string | null;
  Case: EntityRef | null;
  Space: EntityRef | null;
  Cooperative: EntityRef | null;
  RequestType: Option | null;
  CasePriority: Option | null;
  State: Option | null;
  CreatedOn: string | null;
  ModifiedOn: string | null;
};

export type PurchaseInvoiceMonthInformation = {
  TotalAmount: Money | null;
  Month: number;
  Year: number | null;
};

export type PurchaseInvoiceBaseModel = {
  Id: string;
  SellerInvoiceNumber: string | null;
  SellerOrganisationName: string | null;
  BuyerAccountNumber: EntityRef | null;
  AccountingDate: string | null;
  InvoiceDueDate: string | null;
  TotalAmount: Money | null;
  PaymentStatus: Option | null;
  RejectComment: Option | null;
};

export type MenuItem = {
  Id: string;
  Label: string;
  Order: number;
  Parent: string | null;
};

export type CaseSpaceRenovation = {
  Organization: EntityRef | null;
  BuildingAddress: string | null;
  SpaceTypeDescription: Option | null;
  IncludeInCooperativeReport: boolean | null;
  ResolutionDescription: string | null;
  CooperativeIds: string[];
  Building: EntityRef | null;
  EstimatedTiming: string | null;
  ProjectStatus: Option | null;
  PublicDescription: string | null;
  Id: string | null;
  LogicalName: string | null;
  Title: string | null;
  RequestType: Option | null;
  Priority: Option | null;
  Cooperative: EntityRef | null;
  Space: EntityRef | null;
  Status: Option | null;
  State: Option | null;
  CreatedOn: string | null;
  ModifiedOn: string | null;
  ContactPerson: EntityRef | null;
  DescriptionTerms: string | null;
  RenovationTypes: RenovationTypeViewModel[];
};

export type UserSpace = {
  OtherSpaces: string | null;
  ControlledByCooperativeStartDate: string | null;
  ControlledByCooperative: Option | null;
  ControlRightWidow: Option | null;
  RedemptionRightHitas: Option | null;
  CooperativeRentingSpace: Option | null;
  RedemptionRightCity: Option | null;
  ResidenceForMarriedCouple: Option | null;
  RedemptionRightOther: Option | null;
  RegistrationDate: string | null;
  OwnershipPersentage: string | null;
  IsCanReadRegistrationDate: boolean | null;
  IsCanReadOwnershipPersentage: boolean | null;
  IsCanReadShareNumbers: boolean | null;
  IsCanReadShareAmount: boolean | null;
  IsCanReadShareNumberOfVotes: boolean | null;
  Id: string;
  LogicalName: string | null;
  SpaceSortField: string | null;
  RoomsNumberOf: number | null;
  SquareMeters: number | null;
  PetsInSpace: Option | null;
  Cooperative: EntityRef | null;
  Building: EntityRef | null;
  CooperativeIds: string[];
  Name: string | null;
  Type: Option | null;
  Status: Option | null;
  TypeDescription: string | null;
  TypeText: string | null;
  Stairway: string | null;
  Floor: string | null;
  UsageTypeGroup: Option | null;
  CooperativeContractSquareMeters: number | null;
  AdditionalInformation: string | null;
  AddressLine1: string | null;
  SpaceDefiency: string | null;
  ShareNumbers: string | null;
  ShareAmount: number | null;
  ShareNumberOfVotes: number | null;
  ShowMissingRegistrationDateNotification: boolean;
};

export interface BaseNotificationModel {
  Id: string;
  Subject: string | null;
  Message: string | null;
  IsRead: boolean | null;
  Documents: { [key: string]: string } | null;
}

export interface UserNotifcationModel extends BaseNotificationModel {
  CooperativeId: string | null;
  Source: 1 | 2 | 3 | null;
  Subject: string | null;
  ForDate: string | null;
}

export interface InvestorBalanceModel {
  Comments: string | null;
  ModifiedBy: string | null;
  OverSaldo: number | null;
  UnderSaldo: number | null;
}

export interface InvestorLiabilitiesModel {
  BalanceAmount: number | null;
  InHand: number | null;
  TotalAmount: number | null;
}

export type PagingResponseModel = {
  PageNumber: number | null;
  PageSize: number | null;
  TotalCount: number | null;
  HasMore: boolean | null;
};

export type CaseBaseCommon = {
  HasFiles: boolean;
  CaseLevel1: CaseLevel1Option | null;
  CooperativeIds: string[];
  ProjectStatus: Option | null;
  PublicDescription: string | null;
  Description: string | null;
  Id: string;
  LogicalName: string | null;
  Title: string | null;
  RequestType: Option | null;
  Priority: Option | null;
  Cooperative: EntityRef | null;
  Space: EntityRef | null;
  Status: CommonOption | null;
  State: Option | null;
  CreatedOn: string | null;
  ModifiedOn: string | null;
};

export type Building = {
  Id: string;
  LogicalName: string | null;
  Name: string | null;
  ConstructionStartingYear: string | null;
  ConstructionCompletionYear: string | null;
  BuildingInaugurationYear: string | null;
  BuildingType: Option | null;
  Description: string | null;
  StreetAddress: string | null;
  PostalArea: string | null;
  PostalCode: string | null;
  VolumeCubicMetersTotal: number | null;
  FloorsNumberOf: string | null;
  StairwaysNumberOf: number | null;
  BuildingRightUsed: number | null;
  BuildingRightFloorSpaceUsed: number | null;
  BuildingRightPermitted: number | null;
  BuildingRightUnused: number | null;
  BuildingRightApproved: number | null;
  MainBuildingMaterial: EntityRef | null;
  HeatingEnergyType: Option | null;
  AirConditioningSystemType: EntityRef | null;
  HeatingSystemType: EntityRef | null;
  RoofStructureType: EntityRef | null;
  RoofMaterialType: Option | null;
  TVAntennaSystemType: EntityRef | null;
  TVAntennaSystemProvider: EntityRef | null;
  CommunicationNetworkProvider: EntityRef | null;
  CommunicationNetworkType: EntityRef | null;
  CoolingSystemType: Option | null;
  Order: number | null;
  CooperativeIds: string[];
};

export type NetIncomeDashboardModel = {
  CostsIncome: number | null;
  GrossIncome: number | null;
  NetIncome: number | null;
};

export type Lot = {
  Approved: number | null;
  Block: string | null;
  Cooperative: EntityRef | null;
  CooperativeIds: string[];
  CountyOrCity: string | null;
  Id: string;
  RentedOwned: Option | null;
  Lessor: EntityRef | null;
  LotOrEstate: string | null;
  Name: string | null;
  NeihgborhoodOrVillage: string | null;
  OfficialLotID: string | null;
  Order: number | null;
  OriginalLotExisting: EntityRef | null;
  OriginalLotNew: string | null;
  ParkingSpacesPermitted: string | null;
  PostNumber: string | null;
  ReclaimOfLotShare: Option | null;
  RentAmount: number | null;
  RentContractEndDate: string | null;
  RentFeeAdjustmentBasis: string | null;
  SquareMeters: number | null;
  StreetAddress: string | null;
  Unused: string | null;
  Used: string | null;
};

export type OrganizationModel = {
  Id: string;
  LogicalName: string | null;
  Name: string | null;
  PeriodEndingDate: string | null;
  PeriodStartingDate: string | null;
};

export type Version = {
  label: string;
  value: number;
};

export type Cooperative = {
  AdditionalInformation: string | null;
  Id: string;
  LogicalName: string | null;
  Name: string;
  ImagesUrl: string[] | null;
  BusinessEntityCode: string | null;
  ConstructionStartingYear: string | null;
  ConstructionCompletionYear: string | null;
  VATRegistered: Option | null;
  RegisteredToBusinessRegister: string | null;
  ShareAmountTotal: number | null;
  VolumeCubicMeters: number | null;
  FloorsNumberOfLevels: number | null;
  StairwaysAmount: number | null;
  BuildingRightBuissnesSpaceUsed: number | null;
  BuildingRightOtherSpaceUsed: number | null;
  BuildingRightLivingSpaceUnused: number | null;
  BuildingRightPermitted: number | null;
  BuildingRightFloorSpaceUsed: number | null;
  BuildingRightLivingSpaceUsed: number | null;
  TotalBuildingSquareMeters: number | null;
  AddressLine1: string | null;
  AddressLine2: string | null;
  AddressLine3: string | null;
  PostalCode: string | null;
  City: string | null;
  Country: string | null;
  PostOfficeBox: string | null;
  MainBuildingMaterial: EntityRef | null;
  AirConditioningSystemType: EntityRef | null;
  HeatingEnergyType: Option | null;
  HeatingSystemType: EntityRef | null;
  RoofMaterialType: Option | null;
  RoofStructureType: EntityRef | null;
  TVAntennaSystemProvider: EntityRef | null;
  TVAntennaSystemType: EntityRef | null;
  CommunicationNetworkProvider: EntityRef | null;
  CommunicationNetworkType: EntityRef | null;
  MaintenceType: Option | null;
  MaintenanceProvider: {
    Email: string | null;
    Phone: string | null;
    Name: string | null;
    LogicalName: string | null;
    Id: string | null;
  } | null;
  RescuePlanLink: string | null;
  ParkingSpaceTotal: string | null;
  ParkingSpaceGovernanceRules: string | null;
  ParkingSpacesGarage: string | null;
  LastRegisteredDay: string | null;
  SharesMovedDate: string | null;
  RealEstateFederationMembershipId: string | null;
};

export type Portal = {
  Color: null | string;
  Id: string;
  IsCooperativeRelated: boolean;
  IsDisabled: boolean;
  Label: string;
  Name: PortalsName;
  Order: number;
};

// states

export interface AppState {
  loading: boolean;
  isLogIn: boolean;
  portals: Portal[];
  userSettings: User | null;
  privacyPolicy: string | null;
  repairInstruction: string | null;
  repairInstructionEng: string | null;
  warningMessage: string | null;
  loggingOut: boolean;
  logoURL: string | null;
}

export interface HomeState {
  loading: boolean;
  cooperatives: Cooperative[];
  menuConfig: MenuItem[];
}

export interface SettingsState {
  expires_in_start: null | number;
  access_token: null | string;
  refresh_token: null | string;
  token_type: null | string;
  expires_in: null | number;
  current_portal: null | Portal;
  current_cooperative: null | string;
  refer: string;
}

export interface CooperativesState {
  cooperativesList: Cooperative[];
  loading: boolean;
  error: boolean;
}

export interface LotsState {
  lotsList: Lot[];
  loading: boolean;
  error: boolean;
}

export interface BuildingState {
  buildingsList: Building[];
  loading: boolean;
  error: boolean;
}

export interface ConsumptionState {
  spaces: UserSpace[];
  spacesLoading: boolean;
  meters: Meter[];
  metersLoading: boolean;
  consumptions: Consumption[];
  consumptionsLoading: boolean;
  startDate: Date | null;
  endDate: Date | null;
  selectedSpaceId: string | null;
  selectedMeter: Meter | null;
  error: boolean;
  sortParams: SortParams;
}

export interface SpacesState {
  spacesList: Space[];
  loading: boolean;
  error: boolean;
  sortParams: SortParams;
}

export interface KeyOrderState {
  keyOrdersList: KeyOrder[];
  loading: boolean;
  error: boolean;
  sortParams: SortParams;
}

export interface IncidentState {
  sortParams: SortParams;
}

export interface RepairsState {
  repairList: Repair[];
  loading: boolean;
  error: boolean;
  sortParams: SortParams;
  selectedStatus: Option;
}

export interface ServicesState {
  selectedResource: ReservationServiceResourceView | null;
  slotDuration: number | null;
  serviceId: string | null;
}

export interface BankAccountsState {
  bankAccounts: BankAccountView[];
  loading: boolean;
  error: boolean;
  sortParams: SortParams;
}

export interface InvestorBankAccountsState {
  bankAccounts: BankAccountView[];
  loading: boolean;
  error: boolean;
  sortParams: SortParams;
}

export type EventInfoYear = {
  id: string;
  label: string;
};

export interface EventsInformationState {
  eventsInformationList: EventInformation[];
  loading: boolean;
  error: boolean;
  searchTerm: string;
}

export interface FiscalYearsState {
  fiscalYears: FiscalYearsRes | null;
  coopId: string | null;
}

export type Tab = {
  id: string;
  label: string;
  warning: boolean;
};

export interface InvoicesState {
  invoicesList: Invoice[];
  loading: boolean;
  error: boolean;
  sortParams: SortParams;
  coopId: string | null;
}
export interface EventState {
  eventsList: EventModel[];
  loading: boolean;
  error: boolean;
  sortParams: SortParams;
  dateFilter: Option | null;
  prevCoopId: string | null;
}

export interface UserWidgetsState {
  WelcomeWidget: WelcomeWidgetModel | null;
  CaseWidget: CaseBaseModel[] | null;
  ReservationWidget: {} | null;
  NewButtonWidget: NewButtonWidgetModel | null;
  ContactInformationWidget: ContactInformationWidgetModel;
  MessageWidget: EventModel[] | null;
  FinanceWidget: { TotalBalance: number | null } | null;
  CooperativeBalanceSituationWidget: CooperativeBalanceSituationWidgetModel | null;
  CooperativeBudgetWidget: ServerResponseBudgetHistory | null;
  TaskWidget: TaskTodo[] | null;
}

export interface WidgetsState {
  showWelcomeWidget: boolean;
  userWidgets: UserWidgetsState;
  loading: boolean;
  error: boolean;
}

export interface NotificationModel {
  amount: number;
}

export interface UserSpaceNotificationModel extends NotificationModel {
  info: Notification[];
}

export interface ChatEntityInformationModel {
  EntityId: string | null;
  EntityName: string | null;
  Type:
    | 'incident'
    | 'key-order'
    | 'repair'
    | 'eventsinformation'
    | 'task'
    | null;
  NewMessages: number | null;
}

export interface ChatNotificationModel extends NotificationModel {
  info: ChatEntityInformationModel[];
}

export interface NotificationState {
  user_spaces: UserSpaceNotificationModel;
  eventsinformation: ChatNotificationModel;
  incident: ChatNotificationModel;
  key_order: ChatNotificationModel;
  repair: ChatNotificationModel;
  notifications: NotificationModel & { expirationDate: string | null };
  messages: NotificationModel;
  board_eventsinformation: ChatNotificationModel;
  task: ChatNotificationModel;
}

export interface NotificationChangeModel {
  CooperativeId: string | null;
  RegardingObjectRef: EntityRef | null;
  Type: NotificationTypeCode | null;
  ChangeType: 1 | 2 | null;
}

export interface ChangeMessageChangeModel {
  CooperativeId: string | null;
  Regarding: EntityRef | null;
  ChangeType: 1 | 2 | null;
}

export interface PersonInformationState {
  saved: boolean;
  queue: Function[];
}

export interface State {
  app: AppState;
  home: HomeState;
  settings: SettingsState;
  cooperatives: CooperativesState;
  lots: LotsState;
  buildings: BuildingState;
  consumption: ConsumptionState;
  spaces: SpacesState;
  keyOrder: KeyOrderState;
  incidents: IncidentState;
  repairs: RepairsState;
  services: ServicesState;
  bankAccounts: BankAccountsState;
  eventsInformation: EventsInformationState;
  fiscalYears: FiscalYearsState;
  todo: TodoState;
  invoices: InvoicesState;
  events: EventState;
  investorBankAccounts: InvestorBankAccountsState;
  widgets: WidgetsState;
  notifications: NotificationState;
  personInformation: PersonInformationState;
}

export type GetState = () => State;

export interface ReferenceObject {
  clientHeight: number;
  clientWidth: number;
  referenceNode?: Node;

  getBoundingClientRect(): ClientRect;
}

export interface WelcomeWidgetModel {
  DocumentUrl: string | null;
  Image: string | null;
  User: string | null;
  UserRole: string | null;
  Subject: string | null;
  Text: string | null;
  VideoUrl: string | null;
}

export interface NewButtonWidgetModel {
  CanCreateCase: boolean;
  CanCreateKeyOrder: boolean;
  CanCreateRapair: boolean;
  CanCreateReservation: boolean;
  CaseLevels1: CaseLevel1Option[] | null;
  CaseLevels2: CaseLevel2Option[] | null;
}

export interface CooperativeBalanceSituationWidgetModel {
  PaidBalance: number | null;
  PaidBalancePercentage: number | null;
  UnpaidBalance: number | null;
  UnpaidBalancePercentage: number | null;
}

export interface UserWidgetsModel {
  WelcomeWidget: WelcomeWidgetModel | null;
  CaseWidget: {
    Cases: CaseBaseModel[] | null;
  } | null;
  ReservationWidget: {} | null | undefined;
  NewButtonWidget: NewButtonWidgetModel | null;
  ContactInformationWidget: ContactInformationWidgetModel | null;
  MessageWidget: {
    Messages: EventModel[] | null;
  } | null;
  TaskWidget: {
    Tasks: TaskTodo[] | null;
  } | null;
  FinanceWidget: {
    TotalBalance: number | null;
  } | null;
  CooperativeBalanceSituationWidget: CooperativeBalanceSituationWidgetModel | null;
  CooperativeBudgetWidget: ServerResponseBudgetHistory | null;
}

export interface BellNotifications {
  SystemCount: number | null;
  UserCount: number | null;
  TotalCount: number | null;
  MinDueDate: string | null;
}

export interface MessagesNotifications {
  UserCount: number | null;
}

export interface MassMessageSpace {
  Id: string;
  Name: string;
  Order: string;
}

export interface MassMessageBuilding {
  Id: string;
  Name: string;
  Spaces: MassMessageSpace[];
}

export interface MassMessageCooperative {
  Id: string;
  Name: string;
  Buildings: MassMessageBuilding[];
}

export interface MassMessageRoleRes {
  DefaultSelected: boolean;
  Id: string;
  Name: string;
  Order: number;
  RelatedTo: number;
}

export type RoleGroupUniqueName =
  | 'board'
  | 'owners'
  | 'residents'
  | 'PMCroles'
  | 'auditing'
  | 'otherParties';
export interface MassMessageRolesGroupRes {
  Items: MassMessageRoleRes[];
  Name: string;
  Order: number;
  ShowEmptyEntries: number;
  UniqueName: RoleGroupUniqueName | null;
}

export enum RoleEntityType {
  Group,
  Role,
}

export interface MassMessageRole extends MassMessageRoleRes {
  Type: RoleEntityType.Role;
}

export interface MassMessageRolesGroup {
  Name: string;
  Order: number;
  Type: RoleEntityType.Group;
  Roles: MassMessageRole[];
  UniqueName: RoleGroupUniqueName;
  CanExpand: boolean;
}

export interface MassMessageEntityWithRecipients {
  Id: string;
  Name: string;
  Recipients: MassMessageRecipient[];
  Type: ConnectionTargetTypeCode;
  Order: string | null;
}

export enum MassMessageStatusCode {
  Draft = 1,
  Cancelled = 752560002,
  Distributing = 752560000,
  PendingSend = 752560003,
  Distributed = 752560001,
}

export type SelectOption<Value extends string | number> = {
  label: string;
  value: Value;
};
